import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { ConnectionIssue } from 'legacy/lut/components/widgets/public/ConnectionIssue'
import { fetchRawContactFlags } from 'legacy/lut/services/contacts/contactFlags'

import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { IContactFlagsView } from 'clickNgo/models/crud/views'
import { IForm } from 'core-components/classes/models/IForm'
import { NotificationController } from 'core-components/library/popup'
import { IDataProviderFilter, Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

import { ICurrentVariant } from './models'

const formContactFlagsData = (data: IContactFlagsView[]) =>
  data.map((item) => {
    return {
      value: { id: item.Code, caption: item.Code },
      comment: '',
    }
  })

const getUserRightsAndCampaigns = async ({
  contactId,
  partnerId,
  topPartnerId,
}: {
  contactId: string
  partnerId: string
  topPartnerId: string
}) => {
  const getContactFlagsByType = (type: ProfilingCodeType) =>
    fetchRawContactFlags({
      contactId,
      partnerId,
      topPartnerId,
      type,
      options: {
        paging: { page: 0, pageSize: 1000 },
        cacheKey: contactId + partnerId + type,
      },
    })

  const userRights = await getContactFlagsByType(ProfilingCodeType.UserRights)
  const contactCampaigns = await getContactFlagsByType(ProfilingCodeType.Campaigns)
  const tools = await getContactFlagsByType(ProfilingCodeType.Tools)

  return { userRights, contactCampaigns, tools }
}

const getContactCodes = async ({
  contactId,
  partnerId,
  topPartnerId,
  type,
}: {
  contactId: string
  partnerId: string
  topPartnerId: string
  type: string
}) => {
  const result = await getUserRightsAndCampaigns({ contactId, partnerId, topPartnerId })
  let newData: ICurrentVariant[] = []

  if (result) {
    if (type === 'UserRights') {
      newData = formContactFlagsData(result.userRights)
    } else if (type === 'Campaigns') {
      newData = formContactFlagsData(result.contactCampaigns)
    } else if (type === 'Tools') {
      newData = formContactFlagsData(result.tools)
    }
  }

  return newData
}

const saveContactCodesRequests = async (
  newDataRequests: ICurrentVariant[],
  type: string,
  contactId: string,
  Market: string,
  Country: string
) => {
  const crud = CrudStore.get(CrudStoreItem.ContactCodeRequests, 'getContactDataRequests')

  const data = newDataRequests
    .filter((item) => item.status === 'New')
    .map((item) => ({
      ContactId: contactId,
      Code: item.value.id,
      Status: item.status,
      Action: item.action || '',
      Caption: item.value.caption,
      Type: type,
      Comment: item.comment,
      Modified: item.lastActionDate,
    }))

  const oldItems = crud
    .getDataFromStore()
    .filter((v) => v.Type === type && v.ContactId === contactId)

  const updateData: IForm[] = []
  const createData: IForm[] = []

  data.map(async (item) => {
    const oldItem = oldItems.find(
      (v) => v.Code === item.Code && v.Status !== 'Done' && v.Status !== 'Rejected'
    )
    if (oldItem) {
      if (
        oldItem.Code !== item.Code ||
        oldItem.Action !== item.Action ||
        oldItem.Status !== item.Status
      ) {
        updateData.push({
          ...item,
          EntryId: oldItem.EntryId,
          Market: Market || oldItem.Market,
          Country: Country || oldItem.Country,
        })
      }
    } else {
      createData.push({ ...item, Market, Country })
    }
  })

  if (updateData.length) {
    await crud.update('514', updateData)
  }
  if (createData.length) {
    await crud.create('514', createData)
  }

  for (const item of data) {
    const oldItem = oldItems.find(
      (oldItem) => item.Code === oldItem.Code && oldItem.Status === 'Rejected'
    )
    if (oldItem) {
      await crud.delete('514', oldItem)
    }
  }

  if (oldItems) {
    await oldItems.forEach(async (item) => {
      const temp = data.find((v) => v.Code === item.Code)
      if (!temp) {
        await crud.delete('514', item)
      }
    })
  }
}

const getContactCodesRequests = async (contactId: string, type: string) => {
  const crudUserRights = CrudStore.get(CrudStoreItem.ContactCodeRequests, 'getContactDataRequests')

  // We should show the flag as active if it was done in the last 48 hours
  const doneStatusModifiedDate = new Date(new Date().getTime() - 1000 * 60 * 60 * 48).toISOString()

  const filterByStatus: IDataProviderFilter = {
    logic: Logic.AND,
    filters: [
      {
        logic: Logic.OR,
        filters: [
          {
            field: 'Status',
            operator: Operator.NOTEQUAL,
            value: 'Done',
          },
          {
            field: 'Modified',
            operator: Operator.GTE,
            value: doneStatusModifiedDate,
          },
        ],
      },
      {
        field: 'Status',
        operator: Operator.NOTEQUAL,
        value: 'Rejected',
      },
    ],
  }

  const filterUserRights = {
    logic: Logic.AND,
    filters: [
      {
        field: 'ContactId',
        operator: Operator.EQUAL,
        value: contactId,
      },
      {
        field: 'Type',
        operator: Operator.CONTAINS,
        value: type,
      },
      filterByStatus,
    ],
  }

  crudUserRights.setFilter(filterUserRights)
  const result = (await crudUserRights.fetch()).items

  let data: ICurrentVariant[] = []
  if (result) {
    data = result
      .filter((item) => item.ContactId === contactId)
      .map((item) => ({
        value: {
          id: item.Code,
          caption: item.Caption,
        },
        status: item.Status,
        comment: item.Comment,
        action: item.Action,
        lastActionDate: item.Modified,
      }))
  } else {
    NotificationController.show({
      content: () => <ConnectionIssue />,
    })
  }

  return data
}

export { getContactCodes, getContactCodesRequests, saveContactCodesRequests }
